import { useCallback, useEffect, useState } from 'react'

import { API } from '@/api'
import { TeamMemberStatUser } from '@/api/type-response'
import { TeamRole } from '@/consts/team'
import { useMemberStat } from '@/stores/team'
import { formatBytesToGB, formatDurationToHours, formatPrice } from '@/utils/formatter'

export type ResourceType = 'instance' | 'snapshot' | 'datadrive'

export interface MemberResourceStat {
  rank: string
  id: string
  name: string
  nickname?: string
  role: TeamRole
  user: TeamMemberStatUser
  // Instance specific
  instanceNumber?: number
  cost?: string
  duration?: string
  // Snapshot specific
  snapshotNumber?: number
  storageUsed?: string
  storageQuota?: string
  percentage?: string
  // Datadrive specific
  region?: number
}

interface UseTeamMemberResourceStatsOptions {
  teamResID?: string
  resourceType: ResourceType
}

/**
 * Hook to fetch team member resource statistics
 * Supports three resource types: instance, snapshot, datadrive
 */
export function useTeamMemberResourceStats(options: UseTeamMemberResourceStatsOptions) {
  const { teamResID, resourceType } = options

  const [data, setData] = useState<MemberResourceStat[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const memberStat = useMemberStat()

  const fetchStats = useCallback(async () => {
    if (!teamResID) {
      setData([])
      return
    }

    setLoading(true)
    setError(null)

    try {
      let stats: MemberResourceStat[] = []

      if (resourceType === 'instance') {
        const res = await API.getTeamStatMemberInstances({
          res_id: teamResID,
          page: 1,
          per_page: memberStat?.membersCount ?? 100,
        })

        stats = res.stats.map((stat) => ({
          rank: '',
          id: stat.user.resID,
          name: stat.user.user.name,
          nickname: stat.user.user.nickname,
          role: stat.user.user.role as TeamRole,
          user: stat.user,
          instanceNumber: stat.unfinishedCount,
          cost: `${formatPrice(stat.costFeeCent, false, stat.costFeeCent > 100 ? 'Credits' : 'Credit')}`,
          duration: `${formatDurationToHours(stat.durationSecond)} Hours`,
        }))
      } else if (resourceType === 'snapshot') {
        const res = await API.getTeamStatMemberSnapshots({
          res_id: teamResID,
          page: 1,
          per_page: memberStat?.membersCount ?? 100,
        })

        stats = res.stats.map((stat) => {
          const percentage =
            stat.spaceQuotaBytes > 0
              ? ((stat.spaceUsageBytes / stat.spaceQuotaBytes) * 100).toFixed(1)
              : '0.0'

          return {
            rank: '',
            id: stat.user.resID,
            name: stat.user.user.name,
            nickname: stat.user.user.nickname,
            role: stat.user.user.role as TeamRole,
            user: stat.user,
            snapshotNumber: stat.availCount,
            storageUsed: `${formatBytesToGB(stat.spaceUsageBytes)} GB`,
            storageQuota: `${formatBytesToGB(stat.spaceQuotaBytes, 0)} GB`,
            percentage: `${percentage}%`,
          }
        })
      } else if (resourceType === 'datadrive') {
        const res = await API.getTeamStatMemberFSSpaces({
          res_id: teamResID,
          page: 1,
          per_page: memberStat?.membersCount ?? 100,
        })

        stats = res.stats.map((stat) => {
          const percentage =
            stat.spaceQuotaBytes > 0
              ? ((stat.spaceUsageBytes / stat.spaceQuotaBytes) * 100).toFixed(1)
              : '0.0'

          return {
            rank: '',
            id: stat.user.resID,
            name: stat.user.user.name,
            nickname: stat.user.user.nickname,
            role: stat.user.user.role as TeamRole,
            user: stat.user,
            region: stat.availRegions,
            storageUsed: `${formatBytesToGB(stat.spaceUsageBytes)} GB`,
            storageQuota: `${formatBytesToGB(stat.spaceQuotaBytes, 0)} GB`,
            percentage: `${percentage}%`,
          }
        })
      }

      setData(stats)
    } catch (err) {
      console.error('Failed to fetch team member resource stats:', err)
      setError(err as Error)
      setData([])
    } finally {
      setLoading(false)
    }
  }, [teamResID, resourceType, memberStat])

  useEffect(() => {
    fetchStats()
  }, [fetchStats])

  return {
    data,
    loading,
    error,
    refetch: fetchStats,
  }
}
